import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { $createParagraphNode, $createTextNode, $getRoot, $insertNodes, FORMAT_TEXT_COMMAND } from 'lexical';

const mountedEditors = new Map();

const theme = {
  paragraph: 'task-rich-text-paragraph',
  text: {
    bold: 'task-rich-text-bold',
    italic: 'task-rich-text-italic',
    underline: 'task-rich-text-underline'
  }
};

const editorConfig = {
  namespace: 'TDemandTaskRichText',
  theme,
  onError(error) {
    console.error('[t-demand] Lexical editor error', error);
  }
};

const InitialHtmlPlugin = ({ html }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const source = String(html || '').trim();
      if (!source) return;
      if (!/<[a-z][\s\S]*>/i.test(source)) {
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(source));
        root.append(paragraph);
        return;
      }
      const parser = new DOMParser();
      const dom = parser.parseFromString(source, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      if (nodes.length) $insertNodes(nodes);
    });
  }, [editor, html]);

  return null;
};

const EditablePlugin = ({ disabled }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return null;
};

const ToolbarPlugin = ({ disabled }) => {
  const [editor] = useLexicalComposerContext();
  const run = (command) => {
    if (disabled) return;
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, command);
  };

  return (
    <div className="task-rich-text-toolbar" aria-label="Ferramentas de texto longo">
      <button type="button" onClick={() => run('bold')} disabled={disabled} title="Negrito">
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => run('italic')} disabled={disabled} title="Italico">
        <em>I</em>
      </button>
      <button type="button" onClick={() => run('underline')} disabled={disabled} title="Sublinhado">
        <span className="task-rich-text-underline">U</span>
      </button>
    </div>
  );
};

const RichTextEditor = ({ initialValue, disabled, placeholder, onHtmlChange }) => (
  <LexicalComposer initialConfig={editorConfig}>
    <div className={`task-rich-text-shell ${disabled ? 'is-readonly' : ''}`}>
      <ToolbarPlugin disabled={disabled} />
      <div className="task-rich-text-surface">
        <RichTextPlugin
          contentEditable={<ContentEditable className="task-rich-text-content" aria-label="Editor de texto longo" />}
          placeholder={<div className="task-rich-text-placeholder">{placeholder || 'Escreva o texto longo aqui...'}</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
      <HistoryPlugin />
      <InitialHtmlPlugin html={initialValue} />
      <EditablePlugin disabled={disabled} />
      <OnChangePlugin
        onChange={(editorState, editor) => {
          editorState.read(() => {
            onHtmlChange($generateHtmlFromNodes(editor));
          });
        }}
      />
    </div>
  </LexicalComposer>
);

const mount = (container) => {
  if (!container || mountedEditors.has(container)) return;
  const inputSelector = container.dataset.richTextInput;
  const input = inputSelector ? document.querySelector(inputSelector) : container.previousElementSibling;
  if (!input) return;

  const root = createRoot(container);
  mountedEditors.set(container, root);
  root.render(
    <RichTextEditor
      initialValue={input.value || ''}
      disabled={container.dataset.readonly === 'true'}
      placeholder={container.dataset.placeholder || ''}
      onHtmlChange={(html) => {
        input.value = html;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }}
    />
  );
};

const mountAll = (root = document) => {
  root.querySelectorAll('[data-rich-text-editor]').forEach(mount);
};

const destroyAll = () => {
  mountedEditors.forEach((root) => root.unmount());
  mountedEditors.clear();
};

window.TDemandRichTextEditors = {
  mount,
  mountAll,
  destroyAll
};
